import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useToasts, pad2, catIcon, countryFlagUrl, fallbackIcon, variantTag, isStock4D, customIconUrl, subscribeTypeIcons, typeIconsLoaded } from "./lib";
import { lottoApi } from "./api";

// โลโก้ยี่กี — ใช้อีโมจินาฬิกาปลุก (Fluent Emoji Flat, MIT) สื่อถึงออกผลทุก 15 นาที
// ไม่ใช้โลโก้แบรนด์จริงจากเน็ต กันปัญหาลิขสิทธิ์/เครื่องหมายการค้า
const YEEKEE_ICON = "fluent-emoji-flat:alarm-clock";

// โหลดรายการหวยครั้งเดียวเพื่อให้ได้ไอคอนที่แอดมินอัปโหลด (หน้าที่ไม่ได้เรียก types เอง เช่น โพยของฉัน)
let iconsRequested = false;
function useCustomIcon(code) {
  const [, force] = useState(0);
  useEffect(() => {
    const off = subscribeTypeIcons(() => force((n) => n + 1));
    if (!iconsRequested && !typeIconsLoaded()) {
      iconsRequested = true;
      Promise.resolve()
        .then(() => lottoApi.types())
        .catch(() => {
          iconsRequested = false; // ครั้งหน้าลองใหม่ได้
        });
    }
    return off;
  }, []);
  return customIconUrl(code);
}

// ป้ายธงประเทศ — โชว์ธงจริงเด่น ๆ (มีขอบ/เงา) แทน emoji ธง ที่บาง OS render ไม่ออก
// ยี่กี (ไม่ผูกกับประเทศ) ใช้อีโมจินาฬิกาปลุก (YEEKEE_ICON) แทน
// เหลือ catIcon เป็นชั้นสำรองสุดท้ายจริง ๆ เท่านั้น (กรณีไม่มีทั้งธง/โลโก้/ไอคอน)
// หวยย่อยในประเทศเดียวกัน (ลาวพัฒนา/VIP/สตาร์ ฯลฯ) ใช้ธงเดียวกัน เลยติดป้ายสั้น ๆ (variantTag)
// ทับมุมล่างธงไว้ ให้แยกชนิดได้โดยไม่ต้องอ่านชื่อเต็ม
export function FlagBadge({ code, category, size = 40, className = "" }) {
  const custom = useCustomIcon(code);
  const url = custom || countryFlagUrl(code);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  const showFlag = url && !failed;
  const isYeekee = String(code || "").startsWith("YEEKEE");
  const iconId = fallbackIcon(code);
  const tag = variantTag(code);
  // หวยหุ้น 4D: ธงประเทศเดียวกับหวยหุ้นปกติ แต่ขอบทอง + ป้ายทอง "4D" มุมขวาบน
  const is4D = isStock4D(code);
  return (
    <div
      className={`lx-flagbadge${is4D ? " is-4d" : ""} ${className}`}
      style={{ width: size, height: size }}
    >
      {showFlag ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : isYeekee ? (
        <Icon icon={YEEKEE_ICON} width={Math.round(size * 0.68)} height={Math.round(size * 0.68)} />
      ) : iconId ? (
        <Icon icon={iconId} width={Math.round(size * 0.55)} height={Math.round(size * 0.55)} className="lx-flagbadge-ic" />
      ) : (
        <Icon icon={catIcon(category)} width={Math.round(size * 0.5)} height={Math.round(size * 0.5)} className="lx-flagbadge-ic" />
      )}
      {is4D && size >= 28 && (
        <span className="lx-flag4d" aria-hidden="true">
          <b style={{ fontSize: Math.max(9, Math.round(size * 0.27)) }}>4D</b>
        </span>
      )}
      {tag && size >= 28 && <span className="lx-flagbadge-tag">{tag}</span>}
    </div>
  );
}

export function Toasts() {
  const items = useToasts();
  if (!items.length) return null;
  return (
    <div className="lx-toasts">
      {items.map((t) => (
        <div key={t.id} className={`lx-toast ${t.kind}`}>
          {t.message}
        </div>
      ))}
    </div>
  );
}

export function Sheet({ open, onClose, title, children, footer }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="lx-overlay" onClick={onClose}>
      <div className="lx-sheet" onClick={(e) => e.stopPropagation()}>
        {title != null && (
          <div className="lx-spread" style={{ marginBottom: 14 }}>
            <h3 className="lx-h2">{title}</h3>
            <button className="lx-x" onClick={onClose} aria-label="close">
              ✕
            </button>
          </div>
        )}
        {children}
        {footer && <div style={{ marginTop: 16 }}>{footer}</div>}
      </div>
    </div>
  );
}

// นับถอยหลังปิดรับแทง
// size="sm" (ค่าเริ่มต้น) — ไอคอนนาฬิกา + ตัวเลขคั่นด้วย ":" กะทัดรัดที่สุด
// size="lg" — กล่องตัวเลขแยกแต่ละหน่วย มีป้ายกำกับเต็ม "วัน/ชั่วโมง/นาที" ใต้ตัวเลข (วินาทีโชว์ตัวเลขอย่างเดียว ไม่มีป้าย)
//   compact=true — ย่อขนาดกล่อง/ตัวอักษรของ size="lg" ให้พอดีพื้นที่แคบ (เช่น การ์ดกริดหน้าแรก) แต่ยังมีคำกำกับครบ
// showSeconds=false — ตัดหน่วยวินาทีออกทั้งหมด (ไม่ใช้ค่าเริ่มต้น เผื่อจุดที่ไม่อยากให้กระพริบถี่)
// warnMin — เหลือน้อยกว่ากี่นาทีถึงเปลี่ยนเป็นสีแดง (ค่าเริ่มต้น 3)
export function Countdown({ clock, className = "", size = "sm", compact = false, showSeconds = true, warnMin = 3 }) {
  const warn = !clock.done && clock.d === 0 && clock.h === 0 && clock.m < warnMin;
  const showDay = clock.d > 0;
  const showHour = clock.d > 0 || clock.h > 0;

  if (clock.done) {
    if (size === "lg")
      return (
        <span className={`lx-cd-closed ${compact ? "compact" : ""} ${className}`}>
          <Icon icon="solar:lock-keyhole-minimalistic-bold" />
          ปิดรับแล้ว
        </span>
      );
    return (
      <span className={`lx-cd ${className}`}>
        <b>ปิดรับ</b>
      </span>
    );
  }

  if (size === "lg") {
    const units = [];
    if (showDay) units.push({ k: "d", v: clock.d, l: "วัน" });
    if (showHour) units.push({ k: "h", v: clock.h, l: "ชั่วโมง" });
    units.push({ k: "m", v: clock.m, l: "นาที" });
    if (showSeconds) units.push({ k: "s", v: clock.s, l: "" });
    return (
      <div className={`lx-cd lg ${compact ? "compact" : ""} ${warn ? "warn" : ""} ${className}`}>
        {units.map((u) => (
          <div className="lx-cd-unit" key={u.k}>
            <span className="lx-cd-unit-val">{pad2(u.v)}</span>
            {u.l && <span className="lx-cd-unit-lbl">{u.l}</span>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <span className={`lx-cd ${warn ? "warn" : ""} ${className}`}>
      <Icon icon="solar:clock-circle-bold" className="lx-cd-ic" />
      {showDay && (
        <>
          <b>{clock.d}วัน</b>
          <i>:</i>
        </>
      )}
      {showHour && (
        <>
          <b>{pad2(clock.h)}</b>
          <i>:</i>
        </>
      )}
      <b>{pad2(clock.m)}</b>
      {showSeconds && (
        <>
          <i>:</i>
          <b>{pad2(clock.s)}</b>
        </>
      )}
    </span>
  );
}

// เลขรางวัล 1 ชุด → ลูกบอลทีละหลัก (ตัวเลขเดิมอยู่ใน aria-label ให้ screen reader อ่านเป็นเลขเต็ม)
const balls = (n) =>
  String(n)
    .split("")
    .map((ch, i) => (
      <i className="lx-ball" key={i} aria-hidden="true">
        {ch}
      </i>
    ));

export function ResultChips({ result, size }) {
  if (!result) return <span className="lx-muted">รอผล</span>;
  const front3 = Array.isArray(result.front3) ? result.front3 : safeArr(result.front3);
  const bottom3 = Array.isArray(result.bottom3) ? result.bottom3 : safeArr(result.bottom3);
  const special = Array.isArray(result.four_d_special) ? result.four_d_special : safeArr(result.four_d_special);
  const consolation = Array.isArray(result.four_d_consolation)
    ? result.four_d_consolation
    : safeArr(result.four_d_consolation);
  const numCls = size === "sm" ? "lx-rnum sm" : "lx-rnum";
  return (
    <div className="lx-result">
      {result.four_d_first && (
        <div className="lx-rblock">
          <div className="lbl">รางวัลที่ 1</div>
          <div className={numCls} role="img" aria-label={String(result.four_d_first)}>{balls(result.four_d_first)}</div>
        </div>
      )}
      {result.four_d_second && (
        <div className="lx-rblock">
          <div className="lbl">รางวัลที่ 2</div>
          <div className={numCls} role="img" aria-label={String(result.four_d_second)}>{balls(result.four_d_second)}</div>
        </div>
      )}
      {result.four_d_third && (
        <div className="lx-rblock">
          <div className="lbl">รางวัลที่ 3</div>
          <div className={numCls} role="img" aria-label={String(result.four_d_third)}>{balls(result.four_d_third)}</div>
        </div>
      )}
      {special.map((n, i) => (
        <div className="lx-rblock" key={`fdsp${i}`}>
          <div className="lbl">เลขพิเศษ</div>
          <div className={numCls} role="img" aria-label={String(n)}>{balls(n)}</div>
        </div>
      ))}
      {consolation.map((n, i) => (
        <div className="lx-rblock" key={`fdcs${i}`}>
          <div className="lbl">เลขปลอบใจ</div>
          <div className={numCls} role="img" aria-label={String(n)}>{balls(n)}</div>
        </div>
      ))}
      {result.top3 && (
        <div className="lx-rblock">
          <div className="lbl">3 ตัวบน</div>
          <div className={numCls} role="img" aria-label={String(result.top3)}>{balls(result.top3)}</div>
        </div>
      )}
      {front3.map((n, i) => (
        <div className="lx-rblock" key={`f${i}`}>
          <div className="lbl">3 ตัวหน้า</div>
          <div className={numCls} role="img" aria-label={String(n)}>{balls(n)}</div>
        </div>
      ))}
      {bottom3.map((n, i) => (
        <div className="lx-rblock" key={`b${i}`}>
          <div className="lbl">3 ตัวล่าง</div>
          <div className={numCls} role="img" aria-label={String(n)}>{balls(n)}</div>
        </div>
      ))}
      {result.top2 && (
        <div className="lx-rblock">
          <div className="lbl">2 ตัวบน</div>
          <div className={numCls} role="img" aria-label={String(result.top2)}>{balls(result.top2)}</div>
        </div>
      )}
      {result.bottom2 && (
        <div className="lx-rblock">
          <div className="lbl">2 ตัวล่าง</div>
          <div className={numCls} role="img" aria-label={String(result.bottom2)}>{balls(result.bottom2)}</div>
        </div>
      )}
    </div>
  );
}

function safeArr(v) {
  if (!v) return [];
  try {
    return typeof v === "string" ? JSON.parse(v) : v;
  } catch {
    return [];
  }
}

// icon = ไอคอนเวกเตอร์จาก Iconify (ชุด solar) แทน emoji เปล่า ๆ — ใส่ป้ายวงกลมนุ่ม ๆ
// หลังไอคอนให้ดูตั้งใจออกแบบ ไม่ใช่ไอคอนลอยเดี่ยว ๆ บนพื้นว่าง
export function Empty({ icon = "solar:inbox-line-bold-duotone", children }) {
  return (
    <div className="lx-empty">
      <div className="lx-empty-ic">
        <Icon icon={icon} width={28} height={28} />
      </div>
      {children}
    </div>
  );
}

export function Skeleton({ h = 84, style }) {
  return <div className="lx-skel" style={{ height: h, ...style }} />;
}

// หน้า "ต้องเข้าสู่ระบบก่อน" — ใช้ร่วมกันทุกหน้าที่ล็อกไว้สำหรับสมาชิก
// icon = อีโมจิประจำหน้า, perks = สิ่งที่จะได้เมื่อเข้าสู่ระบบ, onLogin = ไปหน้า /auth
export function LoginGate({ icon, title, desc, perks = [], onLogin }) {
  return (
    <section className="vx-gate">
      <div className="vx-gate-rays" aria-hidden="true" />
      <div className="vx-gate-seal" aria-hidden="true">
        <Icon icon={icon} width={64} height={64} />
        <span className="vx-gate-lock">
          <Icon icon="solar:lock-keyhole-minimalistic-bold" width={16} height={16} />
        </span>
      </div>
      <h2 className="vx-gate-title">{title}</h2>
      {desc && <p className="vx-gate-desc">{desc}</p>}
      {!!perks.length && (
        <ul className="vx-gate-perks">
          {perks.map((p) => (
            <li key={p}>
              <Icon icon="solar:check-circle-bold" width={18} height={18} />
              {p}
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="lx-btn primary vx-gate-cta" onClick={onLogin}>
        <Icon icon="solar:login-3-bold" width={18} />
        เข้าสู่ระบบ
      </button>
      <button type="button" className="vx-gate-reg" onClick={onLogin}>
        ยังไม่มีบัญชี? <b>สมัครสมาชิกฟรี</b>
      </button>
    </section>
  );
}
